import React, { useRef } from "react";

import { Box, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { dateRangeOptions } from "constants/index";
import dayjs from "dayjs";
import { useDateFormatSelect } from "store";
import type { Currency, DateRangeValue, Filters, ReportMeta } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { EditableField } from "./EditableField";

interface ReportHeaderProps {
  meta: ReportMeta;
  // Per-page heading (falls back to meta at the call site). Edited independently of the global meta.
  title: string;
  subtitle: string;
  // Report context shown on the right of the header — rendered identically in editor and preview.
  period?: DateRangeValue | null;
  currency?: Currency;
  filters?: Filters;
  // Preview/PDF: render everything static (no editing affordances) so canvas and preview match 1:1.
  readOnly?: boolean;
  onTitleChange?: (value: string) => void;
  onSubtitleChange?: (value: string) => void;
  onUpdate?: (updates: Partial<ReportMeta>) => void;
  onLogoFileChange?: (file: File | null) => void;
}

const periodText = (period?: DateRangeValue | null): string => {
  if (!period?.value) return "N/A";
  return dateRangeOptions.find((opt) => opt.value === period.value)?.text ?? String(period.value);
};

const noop = () => {};

export const ReportHeader: React.FC<ReportHeaderProps> = ({
  meta,
  title,
  subtitle,
  period,
  currency,
  filters,
  readOnly = false,
  onTitleChange,
  onSubtitleChange,
  onUpdate,
  onLogoFileChange
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const dateFormat = useDateFormatSelect();

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onUpdate?.({ logo: reader.result as string });
    reader.readAsDataURL(file);
    onLogoFileChange?.(file);
    event.target.value = "";
  };

  const showContext = Boolean(period || currency);
  const asOfText = meta.asOfDate ? dayjs(meta.asOfDate).format(dateFormat) : "";

  return (
    <Box sx={{ bgcolor: "#071f35", p: "28px 28px 24px", position: "relative" }}>
      {!readOnly && (
        <>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
          <Typography
            sx={{
              position: "absolute",
              top: 8,
              right: 12,
              fontSize: 8,
              fontWeight: 600,
              color: "rgba(255,255,255,0.3)",
              letterSpacing: "0.04em"
            }}
          >
            Click to edit
          </Typography>
        </>
      )}

      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ position: "relative", width: 52, height: 52, flexShrink: 0, "&:hover .logo-remove": { opacity: 1 } }}>
          <Box
            onClick={
              readOnly
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    fileRef.current?.click();
                  }
            }
            title={readOnly ? undefined : meta.logo ? "Change logo" : "Upload logo"}
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "8px",
              border: meta.logo || readOnly ? "none" : "1px dashed rgba(255,255,255,0.3)",
              bgcolor: meta.logo ? "rgba(255,255,255,0.12)" : readOnly ? "transparent" : "rgba(255,255,255,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: readOnly ? "default" : "pointer",
              overflow: "hidden"
            }}
          >
            {meta.logo ? (
              <Box
                component="img"
                src={meta.logo}
                alt="Logo"
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : readOnly ? null : (
              <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.45)", fontWeight: 700 }}>LOGO</Typography>
            )}
          </Box>
          {!readOnly && meta.logo && (
            <Box
              className="logo-remove"
              component="button"
              onClick={(e) => {
                e.stopPropagation();
                onUpdate?.({ logo: "" });
                onLogoFileChange?.(null);
              }}
              sx={{
                position: "absolute",
                top: -6,
                right: -6,
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: "none",
                bgcolor: "#DC2626",
                color: "#FFFFFF",
                fontSize: 9,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 150ms ease-out",
                zIndex: 1
              }}
            >
              {"✕"}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              color: "rgba(255,255,255,0.5)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: "6px"
            }}
          >
            Portfolio Report
          </Typography>
          <EditableField
            value={title}
            placeholder="Report title"
            onSave={onTitleChange ?? noop}
            readOnly={readOnly}
            maxLength={100}
            style={{ fontSize: 22, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.25 }}
          />
          {(!readOnly || subtitle) && (
            <EditableField
              value={subtitle}
              placeholder="Add subtitle"
              onSave={onSubtitleChange ?? noop}
              readOnly={readOnly}
              maxLength={150}
              style={{ fontSize: 13, color: "rgba(255,255,255,0.68)", marginTop: 4 }}
            />
          )}
        </Box>

        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.55)", mb: "3px" }}>
            As of Date
          </Typography>
          {readOnly ? (
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#FFFFFF" }}>{asOfText}</Typography>
          ) : (
            <DesktopDatePicker
              format={dateFormat}
              value={meta.asOfDate ? dayjs(meta.asOfDate) : null}
              onChange={(value) => onUpdate?.({ asOfDate: value ? toIsoMidday(value) : "" })}
              slotProps={{
                textField: {
                  variant: "standard",
                  placeholder: "Select date",
                  InputProps: { disableUnderline: true },
                  sx: {
                    "& .MuiInputBase-input": {
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#FFFFFF",
                      textAlign: "right",
                      p: 0,
                      cursor: "pointer"
                    },
                    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.55)", fontSize: 16 }
                  }
                },
                popper: { sx: { zIndex: 1500 } }
              }}
            />
          )}
          {showContext && (
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.45)", mt: "4px" }}>
              Period: {periodText(period)} | Currency: {currency ?? "N/A"}
            </Typography>
          )}
          {filters && filters.investors.length > 0 && (
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.45)", mt: "2px" }}>
              Investor ({filters.investors.length})
            </Typography>
          )}
          {readOnly
            ? meta.preparedBy && (
                <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.55)", mt: "3px" }}>
                  {meta.preparedBy}
                </Typography>
              )
            : (
              <EditableField
                value={meta.preparedBy}
                placeholder="Prepared by"
                onSave={(v) => onUpdate?.({ preparedBy: v })}
                maxLength={80}
                style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 3, textAlign: "right" }}
              />
            )}
        </Box>
      </Box>
    </Box>
  );
};
